import { useEffect, useRef, useState } from "react";
import { Group, Stack } from "@mantine/core";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import RuleColumnsTable from "../../../../components/RuleColumnsTable/RuleColumnsTable";
import { useRuleColumnsDataQuery } from "../../../../components/RuleColumnsTable/useRuleColumnsData";
import ExportFileButton from "../../../../components/ExportFileButton/ExportFileButton";
import ActivateReportModal from "../../../../components/Modals/ActivateReportModal";
import axios from "../../../../services/axios/config";
import { getAxiosResponseError } from "../../../../utils/functions";
import {
  setSavedSearchPage,
  setSavedSearchTake,
  useSelectSavedSearchApplyNonce,
  useSelectSavedSearchPage,
  useSelectSavedSearchTake,
} from "../../../../redux/features/savedSearchView/savedSearchViewSlice";

export default function SavedSearchResultsTable({ ruleId }) {
  const dispatch = useDispatch();
  const [exportLoading, setExportLoading] = useState(false);

  const applyNonce = useSelectSavedSearchApplyNonce();
  const page = useSelectSavedSearchPage();
  const take = useSelectSavedSearchTake();
  const enabled = applyNonce > 0;

  const containerRef = useRef(null);

  // Each "Run search" click bumps applyNonce; scroll the results section
  // (which lives at the bottom of the page, below the QueryBuilder) into
  // view so the user lands on the freshly-run results.
  useEffect(() => {
    if (applyNonce > 0) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [applyNonce]);

  const { data, error } = useRuleColumnsDataQuery({
    ruleId,
    page,
    take,
    nonce: applyNonce,
    enabled,
  });
  const hasResults = enabled && !error && (data?.rows?.length ?? 0) > 0;

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios({
        url: `search/advanced/export?ruleId=${ruleId}&type=EXCEL`,
        method: "POST",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "file.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toast.error(getAxiosResponseError(err));
    } finally {
      setExportLoading(false);
    }
  };

  if (!enabled) return null;

  return (
    <Stack gap="sm" ref={containerRef}>
      <RuleColumnsTable
        ruleId={ruleId}
        page={page}
        take={take}
        onPageChange={(p) => dispatch(setSavedSearchPage(p))}
        onTakeChange={(t) => dispatch(setSavedSearchTake(t))}
        enabled={enabled}
        nonce={applyNonce}
      />
      {hasResults && (
        <Group justify="flex-end" gap="md">
          <ExportFileButton
            type="EXCEL"
            tooltipLabel="Export in excel"
            loading={exportLoading}
            handleDownload={handleExport}
          />
          <ActivateReportModal id={ruleId} />
        </Group>
      )}
    </Stack>
  );
}
